import { useEffect, useRef, useState } from "react";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { MaskedLine } from "./Reveal";
import { scrollToSection } from "./scroll";
import { useLang } from "@/i18n";
import { Star } from "lucide-react";

const HERO_IMAGES = [
  "/images/logements/villa/43-piscine-palmier-hero.jpg",
  "/images/logements/villa/07-vue-drone-4.jpg",
  "/images/logements/villa/01-piscine-debordement.jpg",
];

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const timer = setInterval(
      () => setIdx((i) => (i + 1) % HERO_IMAGES.length),
      7000
    );
    return () => clearInterval(timer);
  }, [reduceMotion]);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.8]);

  return (
    <section
      id="accueil"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] overflow-hidden grain"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        {HERO_IMAGES.map((src, i) => (
          <motion.img
            key={src}
            src={src}
            alt="Villa Mana — la propriété, piscine à débordement et carbets face à la mer des Caraïbes"
            data-testid={`hero-image-${i}`}
            initial={false}
            animate={{ opacity: i === idx ? 1 : 0, scale: i === idx ? 1.05 : 1.12 }}
            transition={{
              opacity: { duration: 1.8, ease: "easeInOut" },
              scale: { duration: 8.5, ease: "linear" },
            }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ))}
        <motion.div
          style={{ opacity: overlay }}
          className="absolute inset-0 bg-gradient-to-b from-[#0B1410]/80 via-[#0B1410]/40 to-[#0B1410]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1410]/60 via-transparent to-[#0B1410]/30" />
      </motion.div>

      <div className="relative z-10 h-full mx-auto max-w-[1500px] px-5 sm:px-10 flex flex-col justify-end pb-16 sm:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6 font-mono text-[0.65rem] uppercase tracking-[0.32em] text-[#C2A675]"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="font-display font-light text-[#F9F8F3] leading-[1.02] text-[11vw] sm:text-[9vw] lg:text-[7.2vw] tracking-tight">
          <MaskedLine delay={0.25}>
            <span className="block">
              {t.hero.titleA}
              <span
                className="ml-[0.12em] inline-flex items-start gap-[0.035em] whitespace-nowrap align-[0.28em]"
                data-testid="hero-stars"
                aria-label="5 étoiles"
              >
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    aria-hidden="true"
                    className="h-[0.3em] w-[0.3em] text-[#C2A675]"
                    fill="currentColor"
                    strokeWidth={0}
                  />
                ))}
              </span>
            </span>
          </MaskedLine>
          <MaskedLine delay={0.45}>
            <span className="block italic text-[#E6CE9F] font-normal text-[0.55em] leading-[1.15] pt-[0.5em] pb-2">
              {t.hero.titleB}
            </span>
          </MaskedLine>
          {t.hero.titleC ? (
            <MaskedLine delay={0.65}>
              <span className="block italic text-[#E6CE9F] font-normal text-[0.55em] leading-[1.15] pb-2">
                {t.hero.titleC}
              </span>
            </MaskedLine>
          ) : null}
        </h1>

        <div className="mt-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-md text-sm sm:text-base text-[#D0D7D1] font-light leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-book"
              onClick={() => scrollToSection("reservations")}
              className="bg-[#C2A675] text-[#0B1410] hover:bg-[#E6CE9F] px-8 py-4 font-mono text-[0.65rem] uppercase tracking-[0.22em] transition-all duration-300 hover:-translate-y-0.5"
            >
              {t.hero.ctaPrimary}
            </button>
            <button
              data-testid="hero-cta-discover"
              onClick={() => scrollToSection("villa")}
              className="border border-[#F9F8F3]/30 text-[#F9F8F3] hover:border-[#C2A675] hover:text-[#E6CE9F] px-8 py-4 font-mono text-[0.65rem] uppercase tracking-[0.22em] transition-all duration-300"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute top-28 right-5 sm:right-10 z-10 hidden sm:block"
      >
        <div
          data-testid="hero-badge"
          className="bg-[#0B1410]/55 backdrop-blur-[18px] border border-[#C2A675]/25 px-5 py-4 text-center"
        >
          <p className="font-mono text-[0.6rem] uppercase tracking-[0.28em] text-[#C2A675]">
            {t.hero.badge}
          </p>
          <p className="font-display italic text-[#D0D7D1] text-sm mt-2">
            {t.hero.badgeSub}
          </p>
        </div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToSection("villa")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#D0D7D1] hover:text-[#E6CE9F] transition-colors"
      >
        <span className="font-mono text-[0.55rem] uppercase tracking-[0.3em]">
          {t.hero.scroll}
        </span>
        <span className="block w-px h-10 bg-gradient-to-b from-[#C2A675] to-transparent animate-scroll-cue" />
      </motion.button>
    </section>
  );
}
